import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Creator } from "@/entities/Creator";
import { Product } from "@/entities/Product";
import { ArrowRight, Star, Play, Sparkles, TrendingUp, Users, ShoppingBag, Zap } from "lucide-react";
import FeaturedCreatorCarousel from "@/components/FeaturedCreatorCarousel";

const CATEGORIES = [
  { name: "Artist (Entertainment)", icon: "🎨", image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&h=300&fit=crop" },
  { name: "Content Creator", icon: "🎥", image: "https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?w=400&h=300&fit=crop" },
  { name: "Craftsman (Build To Spec)", icon: "🔨", image: "https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=400&h=300&fit=crop" },
  { name: "Healer (Remedies; Protocol)", icon: "🌿", image: "https://images.unsplash.com/photo-1515377905703-c4788e51af15?w=400&h=300&fit=crop" },
  { name: "Manifestor", icon: "✨", image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop" },
  { name: "Motivator", icon: "🔥", image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop" },
  { name: "Soul Worker (Passion Service)", icon: "💫", image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop" },
  { name: "Problem Solver", icon: "🧩", image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop" },
  { name: "Visionary", icon: "🔭", image: "https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=400&h=300&fit=crop" },
];

export default function Home() {
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      Creator.filter({ is_featured: true }, "-total_sales", 8),
      Product.filter({ is_featured: true, status: "active" }, "-createdAt", 6),
    ]).then(([creators, products]) => {
      setFeaturedCreators(creators);
      setFeaturedProducts(products);
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#faf9f7]">
      {/* ── HERO ── */}
      <section className="relative bg-[#1a1a1a] overflow-hidden">
        <div className="absolute inset-0 opacity-20"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1600&h=900&fit=crop)", backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a] via-[#1a1a1a]/90 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-4">Creators Warehouse</p>
          <h1 className="font-display text-5xl md:text-7xl font-bold text-white leading-tight mb-6 max-w-2xl">
            Where Creators
            <span className="block text-[#c8a96e]">Are The Assets</span>
          </h1>
          <p className="text-gray-300 text-lg leading-relaxed mb-10 max-w-lg">
            Every piece tells a story. Every offer solves a problem. Every purchase builds economy.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to={createPageUrl("Creators")} className="inline-flex items-center gap-2 bg-[#c8a96e] hover:bg-[#a8893e] text-white font-semibold px-8 py-3.5 rounded-full transition-colors text-sm">
              Meet the Creators <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={createPageUrl("Shop")} className="inline-flex items-center gap-2 border border-white/30 hover:border-[#c8a96e] text-white hover:text-[#c8a96e] font-semibold px-8 py-3.5 rounded-full transition-colors text-sm">
              Browse Shop
            </Link>
          </div>
          {/* Stats */}
          <div className="flex flex-wrap gap-8 mt-12">
            {[["10+", "Services"], ["30+", "Creators"], ["1k+", "Products"]].map(([num, label]) => (
              <div key={label}>
                <div className="font-display text-3xl font-bold text-white">{num}</div>
                <div className="text-gray-400 text-xs uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURED CREATORS CAROUSEL ── */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-1">Spotlight</p>
            <h2 className="font-display text-3xl font-bold text-[#1a1a1a]">Featured Creators</h2>
          </div>
          <Link to={createPageUrl("Creators")} className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#c8a96e] hover:gap-2 transition-all">
            View All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <FeaturedCreatorCarousel creators={featuredCreators} loading={loading} />
      </section>

      {/* ── BROWSE BY CATEGORY ── */}
      <section className="bg-white border-y border-[#e8e4de] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-1">Explore</p>
            <h2 className="font-display text-3xl font-bold text-[#1a1a1a]">Browse by Creator Type</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {CATEGORIES.map(cat => (
              <Link
                key={cat.name}
                to={createPageUrl(`Shop?category=${encodeURIComponent(cat.name)}`)}
                className="group relative rounded-xl overflow-hidden aspect-square hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10" />
                <div className="absolute inset-0 flex flex-col items-center justify-end p-3 text-center">
                  <span className="text-2xl mb-1">{cat.icon}</span>
                  <p className="text-white text-[11px] font-semibold leading-tight">{cat.name}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CREATORS WAREHOUSE ── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-10">
          <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-1">The Platform</p>
          <h2 className="font-display text-3xl font-bold text-[#1a1a1a]">Built for Creators. Powered by Community.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: <Users className="w-6 h-6 text-[#c8a96e]" />, title: "Creator Profiles", desc: "Every creator gets a full profile with video intro, projects, products and offerings." },
            { icon: <TrendingUp className="w-6 h-6 text-[#c8a96e]" />, title: "Reputation Score", desc: "Scores are earned through completed projects, sales, and community engagement." },
            { icon: <Sparkles className="w-6 h-6 text-[#c8a96e]" />, title: "Offerings & Services", desc: "Beyond products — book consultations, workshops, custom orders and more." },
            { icon: <Zap className="w-6 h-6 text-[#c8a96e]" />, title: "Real Economy", desc: "Every purchase directly supports an independent creator and builds real community wealth." },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-[#e8e4de] rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-[#faf9f7] rounded-xl flex items-center justify-center mb-4">{f.icon}</div>
              <h3 className="font-semibold text-[#1a1a1a] mb-2">{f.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURED PRODUCTS ── */}
      {featuredProducts.length > 0 && (
        <section className="bg-white border-t border-[#e8e4de] py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-1">Shop</p>
                <h2 className="font-display text-3xl font-bold text-[#1a1a1a]">Featured Products</h2>
              </div>
              <Link to={createPageUrl("Shop")} className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#c8a96e] hover:gap-2 transition-all">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {featuredProducts.map(product => (
                <Link key={product.id} to={createPageUrl(`ProductDetail?id=${product.id}`)} className="group">
                  <div className="aspect-square rounded-xl overflow-hidden bg-[#f5f3ef] mb-2">
                    <img src={product.images?.[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <p className="text-xs font-semibold text-[#1a1a1a] line-clamp-1 group-hover:text-[#c8a96e] transition-colors">{product.name}</p>
                  <p className="text-xs text-gray-500">${product.price}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── JOIN CTA ── */}
      <section className="bg-[#1a1a1a] py-20">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-[#c8a96e] text-xs font-semibold uppercase tracking-widest mb-3">Join the Movement</p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">Your Story Deserves a Stage</h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-xl mx-auto">
            Apply to become a creator. Our team will produce your video intro, build your profile, and connect you with a community ready to support your work.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to={createPageUrl("CreatorDashboard")} className="inline-flex items-center gap-2 bg-[#c8a96e] hover:bg-[#a8893e] text-white font-semibold px-8 py-3.5 rounded-full transition-colors text-sm">
              Apply to Join <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={createPageUrl("Shop")} className="inline-flex items-center gap-2 border border-white/20 hover:border-[#c8a96e] text-white hover:text-[#c8a96e] font-semibold px-8 py-3.5 rounded-full transition-colors text-sm">
              <ShoppingBag className="w-4 h-4" /> Browse the Shop
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}